/**
 * SIMULATED SFU PROVIDER — a full `MediaClient` that behaves like the real
 * thing (subscription plans are honoured, unsubscribed publishers produce no
 * frames, speakers rotate, stats move) without needing a live LiveKit server.
 *
 * Local capture is REAL (getUserMedia / getDisplayMedia); only the remote
 * participants are simulated. Replacing this with the LiveKit implementation
 * touches no UI file.
 */
import type {
  ConnectOptions, DeviceKind, MediaClient, MediaParticipant, MediaSnapshot,
  ScreenShareHint, ShareStartOptions, SubscriptionPlan, VideoSource,
} from './contract';
import { createLocalMedia, type LocalMediaController } from './localMedia';
import { initialSnapshot, type MediaRoomSeed } from './roomSeed';
import { blockingPresenter, nextShareClaim } from './shareArbiter';
import { speakerPatch, statsPatch } from './simulatedRoom';
import { createSimulatedVideoSource, type SimulatedVideoSource } from './simulatedVideo';

/** The seed is provider-agnostic; the name is kept for existing importers. */
export type SimulatedRoomSeed = MediaRoomSeed;

const SPEAKER_ROTATE_MS = 3_200;
const STATS_TICK_MS = 1_000;
const EMPTY_PLAN: SubscriptionPlan = { video: new Map(), audio: [], screenShare: false };

export function createSimulatedMediaClient(seed: SimulatedRoomSeed): MediaClient {
  const listeners = new Set<() => void>();
  const local: LocalMediaController = createLocalMedia(() =>
    emit({ processing: local.processing }),
  );
  const sources = new Map<string, SimulatedVideoSource>();
  let plan: SubscriptionPlan = EMPTY_PLAN;
  let timers: number[] = [];

  let snapshot: MediaSnapshot = initialSnapshot(seed);

  const emit = (patch: Partial<MediaSnapshot>): void => {
    snapshot = { ...snapshot, ...patch };
    for (const listener of listeners) listener();
  };

  const patchParticipant = (id: string, patch: Partial<MediaParticipant>): void => {
    emit({ participants: snapshot.participants.map((p) => (p.id === id ? { ...p, ...patch } : p)) });
  };

  /** Keyed `<participantId>:<camera|screen>` — a person can publish both. */
  const sourceFor = (key: string, label: string): SimulatedVideoSource => {
    const existing = sources.get(key);
    if (existing) return existing;
    const created = createSimulatedVideoSource(label, key.length + label.charCodeAt(0));
    sources.set(key, created);
    return created;
  };

  const dropSources = (predicate: (participantId: string, kind: string) => boolean): void => {
    for (const [key, source] of sources) {
      const [id = '', kind = 'camera'] = key.split(':');
      if (!predicate(id, kind)) continue;
      source.stop();
      sources.delete(key);
    }
  };

  const rotateSpeakers = (): void => {
    const patch = speakerPatch(snapshot);
    if (patch) emit(patch);
  };

  const tickStats = (): void => emit(statsPatch(plan));

  const stopShare = async (): Promise<void> => {
    local.stopScreenShare();
    patchParticipant(seed.localId, { sharing: false, shareClaimedAt: null });
    emit({ screenShareOwnerId: null, shareCapturing: false });
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => snapshot,

    async connect(options: ConnectOptions) {
      emit({ state: 'connecting', profile: options.profile });
      await local.setAudioProcessing(options.audioProcessing);
      await local.setVideoEffect(options.videoEffect);
      if (options.micEnabled) await local.setMic(true, options.micDeviceId).catch(() => null);
      if (options.cameraEnabled) await local.setCamera(true, options.cameraDeviceId).catch(() => null);
      patchParticipant(seed.localId, {
        cameraOn: options.cameraEnabled && local.cameraStream !== null,
        micOn: options.micEnabled,
      });
      timers = [
        window.setInterval(rotateSpeakers, SPEAKER_ROTATE_MS),
        window.setInterval(tickStats, STATS_TICK_MS),
      ];
      emit({ state: 'connected' });
    },

    async disconnect() {
      for (const timer of timers) window.clearInterval(timer);
      timers = [];
      for (const source of sources.values()) source.stop();
      sources.clear();
      local.stop();
      emit({ state: 'disconnected', activeSpeakerIds: [], downlinkBps: 0 });
    },

    async setMicEnabled(on) {
      await local.setMic(on).catch(() => null);
      patchParticipant(seed.localId, { micOn: on });
    },

    async setCameraEnabled(on) {
      await local.setCamera(on).catch(() => null);
      patchParticipant(seed.localId, { cameraOn: on && local.cameraStream !== null });
    },

    async setDevice(kind: DeviceKind, deviceId) {
      if (kind === 'videoinput') await local.setCamera(true, deviceId).catch(() => null);
      if (kind === 'audioinput') await local.setMic(true, deviceId).catch(() => null);
      // There is no remote audio to re-sink in the simulator; `audiooutput` is
      // honoured by the real provider only.
    },

    setVideoEffect: (preferences) => local.setVideoEffect(preferences),
    setAudioProcessing: (preferences) => local.setAudioProcessing(preferences),

    /* The simulator obeys the SAME rule as the real client — one presenter,
       decided by `shareArbiter` — so a surface developed against the mock cannot
       be built on behaviour the SFU will not honour. */
    async startScreenShare(
      hint: ScreenShareHint,
      takeOver = false,
      options: ShareStartOptions = {},
    ) {
      /* `blockingPresenter`, the same as the real client: a claim already on
         the wire refuses a start even before its track has landed, so a surface
         built here cannot be built on a race the SFU is going to decide the
         other way. */
      if (snapshot.participants.some((p) => p.id === seed.localId && p.shareDisabled)) {
        return { status: 'denied' as const };
      }
      if (!takeOver) {
        const blocking = blockingPresenter(snapshot.participants, seed.localId);
        if (blocking !== null) return { status: 'blocked' as const, presenter: blocking };
      }
      const stream = await local.startScreenShare(hint, snapshot.profile, options);
      if (!stream) return { status: 'started' as const, withAudio: false };
      const track = stream.getVideoTracks()[0];
      if (track) track.addEventListener('ended', () => void stopShare());
      patchParticipant(seed.localId, {
        sharing: true,
        shareClaimedAt: nextShareClaim(Date.now(), snapshot.participants),
      });
      emit({ screenShareOwnerId: seed.localId, shareCapturing: true });
      /* CAPTURED is the honest answer HERE and only here: there is no publish
         step in the simulator to refuse the track, so captured and published
         are the same event. The real client reports `screenAudioPublished`
         (`livekitLocalControls`), and a surface must never be built on the
         difference — see the note on `ShareStartResult.withAudio`. */
      return { status: 'started' as const, withAudio: local.screenAudioAvailable };
    },

    stopScreenShare: stopShare,

    setProfile: (profile) => emit({ profile }),

    applySubscriptions(next) {
      plan = next;
      dropSources((id, kind) => {
        if (id === seed.localId) return false;
        return kind === 'screen' ? !next.screenShare : !next.video.has(id);
      });
    },

    attachVideo(participantId, source: VideoSource, el) {
      if (participantId === snapshot.localId) {
        const stream = source === 'screen' ? local.screenStream : local.cameraStream;
        if (!stream) return () => undefined;
        el.srcObject = stream;
        void el.play().catch(() => undefined);
        return () => {
          if (el.srcObject === stream) el.srcObject = null;
        };
      }
      // A tile only exists when the plan includes it; `applySubscriptions` is
      // what tears a publisher down again (the SFU equivalent of setEnabled(false)).
      const name = snapshot.participants.find((p) => p.id === participantId)?.name ?? 'Guest';
      return sourceFor(`${participantId}:${source}`, name).attach(el);
    },

    getLocalMicTrack() {
      return local.rawMicStream?.getAudioTracks()[0] ?? null;
    },

    async setMicTransmitting(on) {
      patchParticipant(seed.localId, { micOn: on });
    },

    setParticipantVolume() {
      /* Simulated remotes publish no audio, so there is no gain to set. The
       * method exists to keep the two providers interchangeable. */
    },

    setHandRaised(on) {
      patchParticipant(seed.localId, { handRaisedAt: on ? Date.now() : null });
    },

    sendReaction() {
      /* Reactions ride the gateway, not the SFU (ARCH §5) — no media effect. */
    },

    muteParticipant(participantId) {
      patchParticipant(participantId, { micOn: false, forceMuted: true, speaking: false });
    },

    removeParticipant(participantId) {
      dropSources((id) => id === participantId);
      emit({ participants: snapshot.participants.filter((p) => p.id !== participantId) });
    },

    setSpotlight(participantId, on) {
      emit({
        participants: snapshot.participants.map((p) =>
          p.id === participantId ? { ...p, spotlighted: on } : { ...p, spotlighted: false },
        ),
      });
    },

    setRecording(on) {
      emit({
        recording: on ? 'recording' : 'off',
        recordingStartedBy: on ? snapshot.localId : null,
      });
    },
  };
}
